<template>
  <div class="mt-2">
    <template v-if="loaded && !loadError">
      <f-tabs
        ref="adviser-settings-tabs"
        :items="tabItems"
        :tabsVertical="true"
        :withCompletion="false">
        <div slot="tab-0">
          <adviser-settings-contact-info
            :address="data.address"
            :all-provinces="allProvinces"
            :all-regions="allRegions"
            :city="data.city"
            :email="data.email"
            :name="data.name"
            :phone="data.phone"
            :postCode="data.post_code"
            :province-id="data.province_id"
            :surname="data.surname"
            :uk-county-id="data.uk_county_id"
            :uk-region-id="data.uk_region_id" />
        </div>
        <div slot="tab-1">
          <adviser-settings-invoice-info
            :nip="data.nip" />
        </div>
        <div slot="tab-2">
          <adviser-settings-business-card-info
            :adviser-data="{
              adviser_specialisation_id: data.adviser_specialisation_id,
              adviser_status: data.adviser_status,
              adviser_status: data.adviser_status,
              adviser_statyears_of_experienceus: data.years_of_experience,
              association: data.association,
              association_id: data.association_id,
              description: data.description,
              image: data.image,
              image_url: data.image_url,
              short_description: data.short_description,
              specialisations: data.specialisations,
              work_start_date: data.work_start_date
            }"
            :all-associations="allAssociations"
            :all-specialisations="allSpecialisations"
            :max-specialisations-count="data.package.max_specialisations_count"/>
        </div>
        <div slot="tab-3">
          <adviser-settings-experience-info />
        </div>
        <div slot="tab-4">
          <adviser-settings-education-info />
        </div>
        <div slot="tab-5">
          <adviser-settings-email-info
            ref="adviser-settings-email-info"
            :emailMainColor="data.email_main_color"
            :emailLogo="data.email_logo"
            :emailLogoUrl="data.email_logo_url"
            :adviserEmail="data.email" />
        </div>
        <div slot="tab-6">
          <user-settings-passowrd-change />
        </div>
      </f-tabs>
    </template>
    <template v-else>
      <div
        v-if="!loaded && !loadError"
        class="loading-spinner-wrapper">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
      <div class="wrapper mt-3">
        <f-alert
          v-if="loadError"
          visible
          theme="warning">
          <span>
            {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
          </span>
        </f-alert>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';
import AdviserSettingsContactInfo from './AdviserSettingsContactInfo.vue';
import AdviserSettingsInvoiceInfo from './AdviserSettingsInvoiceInfo.vue';
import AdviserSettingsBusinessCardInfo from './AdviserSettingsBusinessCardInfo.vue';
import AdviserSettingsExperienceInfo from './AdviserSettingsExperienceInfo.vue';
import AdviserSettingsEducationInfo from './AdviserSettingsEducationInfo.vue';
import AdviserSettingsEmailInfo from './AdviserSettingsEmailInfo.vue';
import UserSettingsPasswordChange from './UserSettingsPasswordChange.vue';

export default {
  name: 'adviser-settings',
  components: {
    'adviser-settings-contact-info': AdviserSettingsContactInfo,
    'adviser-settings-invoice-info': AdviserSettingsInvoiceInfo,
    'adviser-settings-business-card-info': AdviserSettingsBusinessCardInfo,
    'adviser-settings-experience-info': AdviserSettingsExperienceInfo,
    'adviser-settings-education-info': AdviserSettingsEducationInfo,
    'adviser-settings-email-info': AdviserSettingsEmailInfo,
    'user-settings-passowrd-change': UserSettingsPasswordChange
  },
  computed: {
    ...mapGetters({
      langId: 'getUserLanguageId'
    }),
    tabItems () {
      return [
        {
          title: this.$t('user.changePassword'),
        },
        {
          title: this.$t('adviserSettings.invoiceData'),
        },
        {
          title: this.$t('adviserSettings.myExpertCard'),
        },
        {
          title: this.$t('adviserSettings.workExperience'),
        },
        {
          title: this.$t('adviserSettings.education'),
        },
        {
          title: this.$t('adviserSettings.logoAndColors'),
        },
        {
          title: this.$t('user.changePassword'),
        }
      ]
    },
    isLanguageSet () {
      if (this.$store.getters['getUserHasLanguage']) {
        return true;
      }

      return false;
    }
  },
  watch: {
    isLanguageSet (newValue) {
      if (!newValue) {
        return;
      }

      if (!this.dataFetching) {
        Vue.nextTick(() => {
          this.loadAdditionalData();
        })
      }
    },
  },
  data() {
    return {
      allAssociations: [{
        id: 0,
        name: this.$t('user.other')
      }],
      allAssociationsLoaded: false,
      allExperiences: [],
      allProvinces: [],
      allProvincesLoaded: false,
      allRegions: [],
      allRegionsLoaded: false,
      allSpecialisations: [],
      allSpecialisationsLoaded: false,
      associationsSet: false,
      data: {
        address: '',
        adviser_specialisation_id: null,
        adviser_status: null,
        association: '',
        association_id: null,
        city: '',
        description: '',
        educations: [],
        email: null,
        email_logo: null,
        email_logo_url: null,
        email_main_color: null,
        email_main_color_url: null,
        experiences: [],
        id: 0,
        image: null,
        image_url: null,
        name: '',
        nip: null,
        phone: '',
        post_code: '',
        province_id: 0,
        short_description: '',
        surname: '',
        uk_county_id: null,
        uk_region_id: null,
        work_start_date: null,
        years_of_experience: 0
      },
      dataFetching: false,
      loaded: false,
      loadedExperiences: false,
      loadError: false,
      removeInProgress: false
    }
  },
  mounted() {
    this.$bus.$on('update-adviser-settings', this.submitForm);
    this.$bus.$on('adviser-settings-reload', this.loadItemData);
    this.loadItemData();
    if (this.isLanguageSet) {
      this.loadAdditionalData();
    }
  },
  methods: {
    loadItemData (reload) {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/api/adviser/adviser/item',
        loadedKey: 'loaded',
        errorKey: 'loadError',
        outputKey: 'data',
        pagination: false,
        successAction: (response) => {
          Vue.delete(this.data, 'province');
          if (reload) {
            this.$refs['adviser-settings-email-info'].updateData(response);
          }
        }
      });
    },
    submitForm (formData) {
      let formDataToSend = this.prepareFormDataToSend(formData);
      let endpointUrl = '/api/adviser/adviser/update';

      let fileParams = [];
      if (formData.image) {
        fileParams.push('file');
      }

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('ui.savedChanges'),
        successText: this.$t('adviserSettings.yourDataHasBeenSaved'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('adviserSettings.yourDataModifyError'),
        successAction: () => {
          this.loaded = false;
          this.loadItemData();
        }
      }, fileParams);
    },
    prepareFormDataToSend (formData) {
      let formDataToSend = JSON.parse(JSON.stringify(this.data));

      let fieldsToSave = Object.keys(formData);

      for (let i = 0; i < fieldsToSave.length; i++) {
        formDataToSend[fieldsToSave[i]] = formData[fieldsToSave[i]];
      }

      return formDataToSend;
    },
    loadAdditionalDataPl () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/provinces/items',
        method: 'get',
        outputKey: 'allProvinces',
        loadedKey: 'allProvincesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    loadAdditionalDataGb () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/uk/regions/items',
        method: 'get',
        outputKey: 'allRegions',
        loadedKey: 'allRegionsLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    loadAdditionalData () {
      this.dataFetching = true;

      if (this.langId === 2) {
        this.loadAdditionalDataGb();
      } else {
        this.loadAdditionalDataPl();
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/specialisations/items',
        method: 'get',
        outputKey: 'allSpecialisations',
        loadedKey: 'allSpecialisationsLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/associations/items',
        method: 'get',
        loadedKey: 'allAssociationsLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        },
        successAction: (response) => {
          this.allAssociations = this.allAssociations.concat(response);
          Vue.nextTick(() => {
            this.associationsSet = true;
          })
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('update-adviser-settings', this.submitForm);
    this.$bus.$off('adviser-settings-reload', this.loadItemData);
  }
}
</script>
